import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  requestUserById,
  setResetStateUserById,
} from "../../../../Redux/UserState/UserActionCreator";
import Loader from "../../../../components/Loader";
import { MdClose } from "react-icons/md";
import { FiUser, FiUsers } from "react-icons/fi";
import { FaMapMarkerAlt } from "react-icons/fa";
import moment from "moment";

const ViewEmployeeModal = ({ isOpen, onClose, userId }) => {
  const dispatch = useDispatch();
  const userDetailsReducer = useSelector((state) => state.UserByIdReducer);
  const [userData, setUserData] = useState(null);

  // Memoized close handler to prevent unnecessary re-renders
  const handleClose = useCallback(() => {
    setUserData(null);
    onClose();
  }, [onClose]);

  // Handle user data updates
  useEffect(() => {
    if (userDetailsReducer?.byIdResponse && !userDetailsReducer?.byIdLoading) {
      setUserData(userDetailsReducer.byIdResponse);
    }
  }, [userDetailsReducer]);

  // Reset user data when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUserData(null);
    }
  }, [isOpen]);

  // Handle errors
  useEffect(() => {
    if (userDetailsReducer?.byIdError && !userDetailsReducer?.byIdLoading) {
      toast.error("Failed to fetch user details. Please try again.");
      handleClose();
    }
  }, [userDetailsReducer?.byIdError, userDetailsReducer?.byIdLoading, handleClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!isOpen) {
        dispatch(setResetStateUserById());
      }
    };
  }, [dispatch, isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return moment(dateString).format("MM-DD-YYYY");
  };

  const getGenderText = (gender) => {
    return gender === "M" ? "Male" : gender === "F" ? "Female" : "Not specified";
  };

  const getStatusText = (status) => {
    return status === "A" ? "Active" : status === "D" ? "Deactive" : "Not specified";
  };

  const getAccountTypeText = (accountType) => {
    return accountType === "ADMIN" ? "Admin" : accountType === "USER" ? "User" : "Not specified";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[80vh] overflow-y-auto m-4">
        <Loader isLoading={userDetailsReducer?.byIdLoading} />
        
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Employee Details</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={userDetailsReducer?.byIdLoading}
          >
            <MdClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-3">
          {userData && (
            <div className="space-y-6">
              {/* Account Information Section */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="text-md font-medium text-slate-400 flex items-center gap-2 mb-4">
                  <FiUser size={18} className="text-cyan-600" /> Account Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        User Name
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.userName || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Email
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.email || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Password
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        ••••••••
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information Section */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="text-md font-medium text-slate-400 flex items-center gap-2 mb-4">
                  <FiUsers size={18} className="text-cyan-600" /> Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        First Name
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.firstName || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Last Name
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.lastName || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Phone Number
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.phone || "Not specified"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Start Date
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {formatDate(userData.joinDate)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        End Date
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {formatDate(userData.endDate)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Date of Birth
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {formatDate(userData.dateOfBirth)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Gender
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {getGenderText(userData.gender)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Status
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {getStatusText(userData.stsCd)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Account Type
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {getAccountTypeText(userData.acctType)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Information Section */}
            <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="text-md font-medium text-slate-400 flex items-center gap-2 mb-4">
                  <FaMapMarkerAlt size={18} className="text-cyan-600" /> Address Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Address Line 1
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.addressLine1 || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Address Line 2
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.addressLine2 || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        City
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.city || "Not specified"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        State
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.state || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Country
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.country || "Not specified"}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 h-10 flex items-center bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Zip Code
                      </legend>
                      <div className="w-full border-none h-full outline-none bg-transparent pt-2 px-2 text-gray-900">
                        {userData.zip || "Not specified"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <div className="relative border border-gray-300 rounded-md w-full border-2 px-2 min-h-[60px] flex items-start bg-gray-50">
                      <legend className="absolute left-2 bg-gray-50 px-1 -top-3 text-sm text-primary">
                        Background Check
                      </legend>
                      <div className="w-full border-none outline-none bg-transparent pt-2 px-2 text-gray-900 mt-2">
                        {userData.backgroundCheck || "Not specified"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewEmployeeModal; 
